import type { CSSProperties, ReactNode } from 'react';
import styles from './Avatar.module.css';

export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg';
export type AvatarVariant = 'filled' | 'neutral' | 'dashed';

export interface AvatarProps {
  /** Display name — used to derive initials when no image. */
  name?: string;
  /** Email — used for the deterministic background color (one color per user). */
  email?: string;
  /** Optional image URL. Falls back to initials on load error. */
  src?: string;
  /** Sizing. Default 'md' (32px). */
  size?: AvatarSize;
  /**
   * Variant.
   *  - filled: colored background, white initials (default — active users)
   *  - neutral: subtle bg, ink2 initials (suspended users / muted contexts)
   *  - dashed: dashed border, no fill (invited users; "no identity yet")
   */
  variant?: AvatarVariant;
  /** Optional accessible label override. */
  ariaLabel?: string;
  /** Optional extra class. */
  className?: string;
}

/**
 * Circular avatar with image + initials fallback. Three variants cover the
 * Users-handoff states (active / suspended / invited).
 *
 * Initials computed from `name` (first letter of each word, max 2 chars).
 * Background color derived deterministically from `email` (or `name` if no
 * email) — same user always gets the same color.
 */
export function Avatar({
  name,
  email,
  src,
  size = 'md',
  variant = 'filled',
  ariaLabel,
  className,
}: AvatarProps) {
  const initials = computeInitials(name);
  const bgColor = variant === 'filled' ? colorForKey(email ?? name ?? '') : undefined;

  const style: CSSProperties = bgColor ? { background: bgColor } : {};
  const labelText = ariaLabel ?? name ?? email ?? 'User avatar';

  const classes = [
    styles.avatar,
    styles[`size_${size}`],
    styles[`variant_${variant}`],
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <span className={classes} style={style} aria-label={labelText} role="img">
      {src && <img className={styles.image} src={src} alt="" />}
      {!src && variant !== 'dashed' && <span className={styles.initials}>{initials}</span>}
    </span>
  );
}

function computeInitials(name?: string): string {
  if (!name) return '';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0]!.charAt(0).toUpperCase();
  return (parts[0]!.charAt(0) + parts[parts.length - 1]!.charAt(0)).toUpperCase();
}

/**
 * Deterministic color from a key. Hash → pick from a small curated palette
 * (warm muted tones that read well with white initials).
 */
function colorForKey(key: string): string {
  const palette = [
    '#3F2BB8', // brand violet
    '#0A7A65', // accent mint
    '#2B6CB0', // sky
    '#B86A0F', // warn orange
    '#5742D4', // brand hi
    '#7C5A2C', // earth
    '#7E4488', // mulberry
    '#3D5671', // ink2 stone
  ];
  let hash = 0;
  for (let i = 0; i < key.length; i++) {
    hash = (hash * 31 + key.charCodeAt(i)) | 0;
  }
  return palette[Math.abs(hash) % palette.length]!;
}

// Re-export the children type so users that wrap Avatar (e.g. AvatarStack)
// can type their children correctly.
export type { ReactNode };
